/** Site UI timezone by ``SERVER_NAME`` (independent of line_configs ``timezone`` used for ASB parsing). */
export const SERVER_DISPLAY_TIMEZONE = {
  'ukhun-hfg': 'Europe/London',
  'aubne-hfg': 'Australia/Sydney',
  demo: 'Europe/Amsterdam',
  nl: 'Europe/Amsterdam',
  nl1: 'Europe/Amsterdam',
  ireland: 'Europe/Dublin',
};

/** Resolve tablet / live-chart display timezone from backend ``server_name`` only. */
export function resolveDisplayTimezone(serverName) {
  if (!serverName) {
    return null;
  }
  return SERVER_DISPLAY_TIMEZONE[String(serverName).trim().toLowerCase()] ?? null;
}

/** Parse backend UTC timestamps (naive ISO or …Z) into a Date instant. */
export function parseUtcInstant(value) {
  if (value == null || value === '') {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  let text = String(value).trim();
  if (!text) {
    return null;
  }
  if (
    /^\d{4}-\d{2}-\d{2}T/.test(text) &&
    !/[zZ]$/.test(text) &&
    !/[+-]\d{2}:\d{2}$/.test(text)
  ) {
    text = `${text}Z`;
  }
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Format a UTC instant for display in the line/site IANA timezone (24h clock). */
export function formatSiteDateTime(value, timeZone) {
  const instant = parseUtcInstant(value);
  if (!instant) {
    return '';
  }
  const options = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  };
  if (timeZone) {
    options.timeZone = timeZone;
  }
  return new Intl.DateTimeFormat('en-GB', options).format(instant);
}

/**
 * Map a UTC instant to a Date whose local wall clock matches the site timezone.
 * Plotly uses browser-local time for axis labels; this keeps labels correct in any browser TZ.
 */
export function utcInstantToSiteWallClockDate(value, timeZone) {
  const instant = parseUtcInstant(value);
  if (!instant || !timeZone) {
    return instant;
  }
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(instant);
  const get = (type) => parts.find((part) => part.type === type)?.value ?? '00';
  const isoLocal = `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}`;
  return new Date(isoLocal);
}

export function resolveLineTimezone(configPayload, lineNo) {
  const line = (configPayload?.line_configs ?? []).find(
    (item) => item?.line_no != null && String(item.line_no) === String(lineNo)
  );
  const lineZone = (line?.items ?? []).find((item) => item?.key === 'timezone')?.value;
  if (lineZone) {
    return String(lineZone);
  }
  const globalZone = (configPayload?.global_items ?? []).find((item) => item?.key === 'TIMEZONE')?.value;
  return globalZone ? String(globalZone) : null;
}
